import { useEffect, useMemo, useRef } from 'react'
import _debounce from 'lodash/debounce'

/**
 * Custom hook that creates a debounced function with automatic cleanup
 * 
 * @param {Function} callback - The function to debounce
 * @param {number} delay - The debounce delay in milliseconds
 * @param {Array} deps - Dependencies array for the callback (similar to useCallback)
 * @returns {Function} The debounced function with a .cancel() method
 * 
 * @example
 * const debouncedSearch = useDebounce((searchTerm) => {
 *   console.log('Searching for:', searchTerm)
 * }, 500, [])
 * 
 * // Use it in an event handler
 * const handleChange = (e) => {
 *   debouncedSearch(e.target.value)
 * }
 */
const useDebounce = (callback, delay, deps = []) => {
    const callbackRef = useRef(callback)

    // Keep callback ref up to date
    useEffect(() => {
        callbackRef.current = callback
    }, [callback])

    // Create debounced function
    const debouncedFn = useMemo(
        () => _debounce((...args) => callbackRef.current(...args), delay),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [delay, ...deps]
    )

    // Cancel on unmount
    useEffect(() => {
        return () => {
            debouncedFn.cancel()
        }
    }, [debouncedFn])

    return debouncedFn
}

export default useDebounce
